import { FieldValues, useFormState, UseFormReturn, useWatch } from 'react-hook-form';

/**
 * Tracks whether a form has unsaved changes vs. the values it was loaded with.
 *
 * The form's own dirtyFields decides which fields are worth looking at — a field it does
 * not flag is equal to its default. Those candidates are then re-checked with a comparison
 * that treats two cases as unchanged which the form's own comparison does not:
 *
 *   1. Empty values. "Field never set" (null / undefined) and "field cleared by the user"
 *      ('') are different to the form but identical to the user, so clearing a field that
 *      arrived empty should not count as a change.
 *   2. Dates. Values loaded from the API are ISO strings with a UTC time component, while
 *      the date picker produces Date objects at local midnight. Same day to the user,
 *      different values to the form — so a "pick the original date back" revert would
 *      otherwise never undirty the form.
 *
 * TODO (DEVF-145): once dates are entered and stored in one canonical form app-wide, the
 * date case disappears and this can read isDirty straight off the form.
 */
export function useFormDirtyTracking<T extends FieldValues>(formApi: UseFormReturn<T>): boolean {
    const { dirtyFields, defaultValues } = useFormState({ control: formApi.control });
    const values = useWatch({ control: formApi.control });

    return Object.keys(dirtyFields).some((name) => toComparableValue(values?.[name]) !== toComparableValue(defaultValues?.[name]));
}

function toComparableValue(value: unknown): string {
    if (value === undefined || value === null || value === '') {
        return '';
    }

    // Reduce dates to the calendar day so the time and timezone carried by the two
    // representations don't register as a change.
    if (value instanceof Date) {
        return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
    }
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
        return value.substring(0, 10);
    }

    return JSON.stringify(value);
}
